"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState, type ReactNode } from "react"
import {
  Home,
  Ticket,
  Package,
  Store,
  MoreHorizontal,
  Activity,
  Swords,
  HeartPulse,
  Send,
  Users2,
  Smartphone,
  Settings,
  LogOut,
  X,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useSidebar } from "@/contexts/sidebar-context"
import { useTenant } from "@/contexts/tenant-context"
import { useAuth } from "@/contexts/auth-context"

interface DockItem {
  id: string
  label: string
  href: string
  icon: typeof Home
  requiredFeature?: string | null
}

// Saúde ganha o slot primário: a checagem diária de "o bot tá vivo?" é tarefa
// de todo dia da operadora; Lojas é configuração ocasional e vive no "Mais".
const PRIMARY_ITEMS: DockItem[] = [
  { id: "overview", label: "Visão", href: "/dashboard-cms", icon: Home },
  { id: "coupons", label: "Cupons", href: "/dashboard-cms/cupons", icon: Ticket },
  { id: "products", label: "Produtos", href: "/dashboard-cms/produtos", icon: Package },
  { id: "health", label: "Saúde", href: "/dashboard-cms/saude", icon: HeartPulse, requiredFeature: "saude" },
]

// Quando o tenant não tem a feature de um slot primário, este assume o lugar.
const FALLBACK_PRIMARY: DockItem = { id: "stores", label: "Lojas", href: "/dashboard-cms/lojas", icon: Store }

const MORE_ITEMS: DockItem[] = [
  { id: "stores", label: "Lojas", href: "/dashboard-cms/lojas", icon: Store },
  { id: "realtime", label: "Tempo real", href: "/dashboard-cms/realtime", icon: Activity, requiredFeature: "realtime" },
  { id: "spy", label: "Spy", href: "/dashboard-cms/spy", icon: Swords, requiredFeature: "spy" },
  { id: "telegram", label: "Telegram", href: "/dashboard-cms/ferramentas/telegram", icon: Send },
  { id: "members", label: "Membros", href: "/dashboard-cms/equipe/membros", icon: Users2, requiredFeature: "membros" },
  { id: "app", label: "App mobile", href: "/dashboard-cms/app", icon: Smartphone },
  { id: "settings", label: "Configurações", href: "/settings", icon: Settings },
]

function isActiveHref(pathname: string | null, href: string): boolean {
  if (!pathname) return false
  if (href === "/dashboard-cms") return pathname === "/dashboard-cms"
  return pathname === href || pathname.startsWith(`${href}/`)
}

function DockButton({
  active,
  onClick,
  href,
  icon,
  label,
}: {
  active: boolean
  onClick?: () => void
  href?: string
  icon: ReactNode
  label: string
}) {
  const inner = (
    <span
      className={cn(
        "relative flex h-full w-full flex-col items-center justify-center gap-0.5 pt-1.5 transition-colors",
        active ? "text-[var(--brand-amber)]" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute -top-px left-1/2 h-[2px] w-7 -translate-x-1/2 rounded-full bg-[var(--brand-amber)]"
        />
      )}
      <span className="flex h-6 w-6 items-center justify-center">{icon}</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.08em] leading-none">{label}</span>
    </span>
  )

  if (href) {
    return (
      <Link
        href={href}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        className="flex h-full min-w-0 flex-1 items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-inset"
      >
        {inner}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-full min-w-0 flex-1 items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-inset"
    >
      {inner}
    </button>
  )
}

function MoreSheet({
  isOpen,
  onClose,
  items,
  pathname,
  tenantName,
  onLogout,
}: {
  isOpen: boolean
  onClose: () => void
  items: DockItem[]
  pathname: string | null
  tenantName: string
  onLogout: () => void
}) {
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [isOpen, onClose])

  return (
    <div
      className={cn(
        "fixed inset-0 z-[80] flex flex-col justify-end transition-opacity duration-200 lg:hidden",
        isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
      )}
      role="dialog"
      aria-modal={isOpen ? "true" : undefined}
      aria-label="Mais opções"
      onClick={onClose}
    >
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 bg-foreground/40 backdrop-blur-sm transition-opacity duration-200",
          isOpen ? "opacity-100" : "opacity-0",
        )}
      />
      <section
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative max-h-[80vh] overflow-y-auto rounded-t-3xl border border-panel-border border-b-0 bg-panel shadow-float-strong transition-transform duration-300 ease-out",
          isOpen ? "translate-y-0" : "translate-y-full",
        )}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
      >
        <div className="flex flex-col items-center px-4 pb-2 pt-3">
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-border/60" />
        </div>

        <div className="flex items-baseline justify-between px-5 pb-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Mais opções
            </p>
            <h2 className="mt-0.5 font-display text-xl italic text-foreground">{tenantName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <ul className="divide-y divide-border/40 px-2">
          {items.map((item) => {
            const Icon = item.icon
            const active = isActiveHref(pathname, item.href)
            return (
              <li key={item.id}>
                <Link
                  href={item.href}
                  onClick={onClose}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex min-h-12 items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors",
                    active
                      ? "bg-[var(--brand-amber)]/8 text-foreground"
                      : "text-foreground/85 hover:bg-muted/40",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-md transition-colors",
                      active ? "text-[var(--brand-amber)]" : "text-muted-foreground",
                    )}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex-1 font-medium">{item.label}</span>
                  {active && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--brand-amber)]">
                      aqui
                    </span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>

        <div className="mt-2 border-t border-border/40 px-2 pt-2">
          <button
            type="button"
            onClick={() => {
              onClose()
              onLogout()
            }}
            className="flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-rose-600 transition-colors hover:bg-rose-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-md">
              <LogOut className="h-[18px] w-[18px]" />
            </span>
            <span className="flex-1 text-left font-medium">Sair</span>
          </button>
        </div>
      </section>
    </div>
  )
}

export default function MobileDock() {
  const pathname = usePathname()
  const router = useRouter()
  const { isMobile, isMobileOpen } = useSidebar()
  const { tenant, hasFeature } = useTenant()
  const { logout } = useAuth()
  const [moreOpen, setMoreOpen] = useState(false)

  // Close the more-sheet when route changes.
  useEffect(() => {
    setMoreOpen(false)
  }, [pathname])

  const primaryItems = PRIMARY_ITEMS.map((item) =>
    !item.requiredFeature || hasFeature(item.requiredFeature) ? item : FALLBACK_PRIMARY,
  )
  const primaryIds = new Set(primaryItems.map((item) => item.id))

  const visibleMore = MORE_ITEMS.filter(
    (item) => (!item.requiredFeature || hasFeature(item.requiredFeature)) && !primaryIds.has(item.id),
  )

  const moreActive = visibleMore.some((item) => isActiveHref(pathname, item.href))

  const hide = !isMobile || isMobileOpen || (pathname?.startsWith("/auth") ?? false)

  return (
    <>
      <nav
        aria-label="Atalhos rápidos"
        className={cn(
          "fixed inset-x-0 bottom-0 z-[55] lg:hidden",
          // Chrome neutro: painel quase sólido + blur leve pra legibilidade
          // sobre conteúdo rolando. Sombra preta neutra e borda do sistema —
          // o âmbar fica reservado pro item ATIVO, onde carrega significado.
          "bg-panel/95 backdrop-blur-md border-t border-panel-border",
          "shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.18)] dark:shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.45)]",
          "transition-transform duration-300 ease-out",
          hide ? "translate-y-full" : "translate-y-0",
        )}
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="mx-auto flex h-14 max-w-md items-stretch">
          {primaryItems.map((item) => {
            const Icon = item.icon
            return (
              <DockButton
                key={item.id}
                active={isActiveHref(pathname, item.href)}
                href={item.href}
                icon={<Icon className="h-[18px] w-[18px]" strokeWidth={2.1} />}
                label={item.label}
              />
            )
          })}
          <DockButton
            active={moreActive || moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
            icon={<MoreHorizontal className="h-[18px] w-[18px]" strokeWidth={2.1} />}
            label="Mais"
          />
        </div>
      </nav>

      <MoreSheet
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        items={visibleMore}
        pathname={pathname}
        tenantName={tenant?.name || "Menu"}
        onLogout={() => {
          logout()
          router.push("/auth/login")
        }}
      />
    </>
  )
}
